import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { AuthProvider } from './context/AuthContext';
import { DashboardProvider } from './context/DashboardContext';
import GlobalStyle from './styles/GlobalStyle';
import theme from './styles/theme';

// Guards
import RequireAuth from './components/guards/RequireAuth';
import RequirePhone from './components/guards/RequirePhone';
import RequireApproval from './components/guards/RequireApproval';
import RequireAdmin from './components/guards/RequireAdmin';

// System / Auth pages
import SplashPage from './pages/System/SplashPage';
import LinkPhonePage from './pages/Auth/LinkPhonePage';
import PendingPage from './pages/Auth/PendingPage';
import FindIdPage from './pages/Auth/FindIdPage';
import FindPasswordPage from './pages/Auth/FindPasswordPage';
import CompleteProfilePage from './pages/Auth/CompleteProfilePage';

// Login / SignUp
import LoginPage from './pages/Login/LoginPage';
import SignUpPage from './pages/Login/SignUpPage';
import AuthLoadingPage from './pages/Login/AuthLoadingPage';
import PhoneVerifyPage from './pages/Login/PhoneVerifyPage';

// User app
import MainLayout from './components/layout/MainLayout';
import HomePage from './pages/Home/HomePage';
import ClassListPage from './pages/Class/ClassListPage';
import ClassDetailPage from './pages/Class/ClassDetailPage';
import CalendarPage from './pages/Calendar/CalendarPage';
import MessagePage from './pages/Message/MessagePage';
import ProfilePage from './pages/Profile/ProfilePage';
import TermsPage from './pages/Profile/TermsPage';
import PrivacyPage from './pages/Profile/PrivacyPage';
import NotificationSettingsPage from './pages/Profile/NotificationSettingsPage';
import AssignmentPage from './pages/Assignment/AssignmentPage';
import MaterialsPage from './pages/Materials/MaterialsPage';
import GradePage from './pages/Grade/GradePage';
import ChatPage from './pages/Chat/ChatPage';
import ChatDetailPage from './pages/Chat/ChatDetailPage';

// Admin
import AdminLoginPage from './pages/Admin/Login/AdminLoginPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/Admin/Dashboard/AdminDashboard';
import AdminUsersPage from './pages/Admin/Users/AdminUsersPage';
import AdminClassesPage from './pages/Admin/Classes/AdminClassesPage';
import AdminClassDetailPage from './pages/Admin/Classes/AdminClassDetailPage';
import AdminSchedulePage from './pages/Admin/Schedule/AdminSchedulePage';
import AdminReportsPage from './pages/Admin/Reports/AdminReportsPage';
import AdminMessagesPage from './pages/Admin/Messages/AdminMessagesPage';
import AdminSettingsPage from './pages/Admin/Settings/AdminSettingsPage';
import AdminMaterialsPage from './pages/Admin/Materials/AdminMaterialsPage';
import AdminUpdatesPage from './pages/Admin/Updates/AdminUpdatesPage';
import AdminNotificationsPage from './pages/Admin/Notifications/AdminNotificationsPage';
import useKeyboardScroll from './hooks/useKeyboardScroll';
import { checkAppUpdate } from './services/appVersionService';

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function VersionChecker() {
  const [toast, setToast] = React.useState('');
  React.useEffect(() => {
    checkAppUpdate({ onUpdate: (content) => setToast(content) });
  }, []);
  if (!toast) return null;
  return (
    <div style={{
      position: 'fixed', bottom: 100, left: '50%', transform: 'translateX(-50%)',
      background: '#fff', padding: '14px 20px', borderRadius: 12,
      fontSize: 14, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 270, maxWidth: 340,
    }}>
      <span style={{ fontSize: 18, color: 'var(--color-primary)' }}>&#9432;</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#111827' }}>새로운 버전으로 업데이트 중...</div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>{toast}</div>
      </div>
    </div>
  );
}

function App() {
  useKeyboardScroll();

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
        <DashboardProvider>
        <Router>
          <ScrollToTop />
          <VersionChecker />
          <Routes>
            {/* 스플래시 (웹 진입점 + WEB_READY + 분기) */}
            <Route path="/" element={<SplashPage />} />

            {/* 공개 라우트 */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/auth-loading" element={<AuthLoadingPage />} />
            <Route path="/phone-verify" element={<PhoneVerifyPage />} />
            <Route path="/find-id" element={<FindIdPage />} />
            <Route path="/find-password" element={<FindPasswordPage />} />

            {/* RequireAuth만 (RequirePhone/RequireApproval 밖) */}
            <Route element={<RequireAuth />}>
              <Route path="/complete-profile" element={<CompleteProfilePage />} />
              <Route path="/link-phone" element={<LinkPhonePage />} />
              <Route path="/pending" element={<PendingPage />} />

              {/* RequireAuth + RequirePhone + RequireApproval */}
              <Route element={<RequirePhone />}>
                <Route element={<RequireApproval />}>
                  <Route element={<MainLayout />}>
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/class" element={<ClassListPage />} />
                    <Route path="/class/:id" element={<ClassDetailPage />} />
                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/message" element={<MessagePage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/notification-settings" element={<NotificationSettingsPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/assignment" element={<AssignmentPage />} />
                    <Route path="/materials" element={<MaterialsPage />} />
                    <Route path="/grade" element={<GradePage />} />
                  </Route>

                  {/* 채팅 (MainLayout 밖 — 전체 화면) */}
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/chat/:roomId" element={<ChatDetailPage />} />
                </Route>
              </Route>
            </Route>

            {/* 관리자 */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="classes" element={<AdminClassesPage />} />
                <Route path="classes/:id" element={<AdminClassDetailPage />} />
                <Route path="materials" element={<AdminMaterialsPage />} />
                <Route path="schedule" element={<AdminSchedulePage />} />
                <Route path="reports" element={<AdminReportsPage />} />
                <Route path="messages" element={<AdminMessagesPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
                <Route path="updates" element={<AdminUpdatesPage />} />
                <Route path="notifications" element={<AdminNotificationsPage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
        </DashboardProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
